import React, { useEffect, useRef, useState } from 'react';
import { Btn, Loading, Msg, fmtDate, panelGet, panelPost } from '../../kit';
import { Badge, Drawer, EmailPreview, Labeled, Modal, fmtFrom, inputCls } from '../emailKit';
import { VaryingNotices } from '../VaryingNotice';
import {
  BodyFormat, EditableContent, Envelope, FORMAT_LABEL, LibraryRow, Rendered, SourceContent, SourceRef,
  toLibraryPayload,
} from './types';

/**
 * "Start from…" (backend: email_library — library_sources lists, library_source
 * loads one as SourceContent, library_render previews it; all read-only).
 *
 * Four tabs: Saved templates (the shared library), Built-in e-mails (the code
 * templates, MEMAC first), Past campaigns (every Campaigns / MEMAC batch) and
 * Email designs. A pick is either a reference to the template itself (only
 * when the caller allows it and the source has a key: a saved or built-in
 * template, or a campaign that sent one) or a copy of its words, in a format
 * the caller's editor takes (converted on the server when it differs). The
 * caller closes the drawer.
 *
 * SaveAsTemplate (below) is the other direction: what is in an editor, saved
 * as a new template in the library.
 */

export type PickerTab = 'library' | 'templates' | 'campaigns' | 'designs';
const TAB_LABEL: Record<PickerTab, string> = {
  library: 'Saved templates', templates: 'Built-in e-mails', campaigns: 'Past campaigns', designs: 'Email designs',
};
const ALL_TABS: PickerTab[] = ['library', 'templates', 'campaigns', 'designs'];
const ALL_FORMATS: BodyFormat[] = ['text', 'html', 'design'];
const MODE_LABEL: Record<string, string> = { template: 'Template', custom: 'Written for the send', design: 'Design' };

type Item = Record<string, any> & { kind: string; ref: string; name: string };

/** The mail a source would send, as library_render reads it: by key when
 *  it has one, else as a draft (no From: the source's own is shown). */
const renderBody = (src: SourceContent, memacCategory?: string): Record<string, any> => {
  const body: Record<string, any> = src.template_key
    ? { key: src.template_key }
    : { draft: {
      body_format: src.format, subject: src.subject, body: src.format === 'design' ? '' : src.body,
      text: src.format === 'html' ? src.text : '', design: src.format === 'design' ? src.design : null,
      ctas: src.format === 'text' ? src.ctas : [], category: src.category, name: src.name,
      placeholder_fallbacks: src.placeholder_fallbacks || {},
    } };
  if (memacCategory) body.memac_category = memacCategory;
  return body;
};

export const TemplatePicker: React.FC<{
  onPick: (src: SourceContent, how: 'reference' | 'copy') => void;
  onClose: () => void;
  /** Offer "use the template itself" (by reference) when the source has a key. */
  allowReference?: boolean;
  tabs?: PickerTab[];
  /** The formats the caller's editor takes; a copy in another one is converted. */
  formats?: BodyFormat[];
  /** Preview with the MEMAC unsubscribe line of this category. */
  memacCategory?: string;
  title?: string;
}> = ({ onPick, onClose, allowReference, tabs = ALL_TABS, formats = ALL_FORMATS, memacCategory, title }) => {
  const [tab, setTab] = useState<PickerTab>(tabs[0] || 'library');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [listErr, setListErr] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [sel, setSel] = useState<Item | null>(null);
  const [src, setSrc] = useState<SourceContent | null>(null);
  const [srcErr, setSrcErr] = useState('');
  const [busy, setBusy] = useState('');
  const [view, setView] = useState<Rendered | null>(null);
  const [viewErr, setViewErr] = useState('');
  const seq = useRef(0);

  useEffect(() => { const t = setTimeout(() => setQLive(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    let gone = false;
    setListLoading(true); setListErr('');
    panelGet('email', 'library_sources', { kind: tab, q: qLive }).then(({ data, error }) => {
      if (gone) return;
      setListLoading(false);
      if (error) { setListErr(error); setItems([]); return; }
      setItems(((data as any)?.items || []) as Item[]);
    });
    return () => { gone = true; };
  }, [tab, qLive]);

  const open = async (it: Item) => {
    const mine = ++seq.current;
    setSel(it); setSrc(null); setSrcErr(''); setView(null); setViewErr('');
    const { data, error } = await panelGet('email', 'library_source', { kind: it.kind, ref: it.ref });
    if (mine !== seq.current) return;
    if (error) { setSrcErr(error); return; }
    const s = data as SourceContent;
    setSrc(s);
    const r = await panelPost('email', 'library_render', renderBody(s, memacCategory));
    if (mine !== seq.current) return;
    if (r.error) setViewErr(r.error); else setView(r.data as Rendered);
  };

  const copyAs = async (f: BodyFormat) => {
    if (!src || !sel) return;
    if (f === src.format) { onPick(src, 'copy'); return; }
    setBusy(f); setSrcErr('');
    const { data, error } = await panelGet('email', 'library_source', { kind: sel.kind, ref: sel.ref, as: f });
    setBusy('');
    if (error) { setSrcErr(error); return; }
    onPick(data as SourceContent, 'copy');
  };

  const canRef = !!(allowReference && src?.template_key);
  const ownOk = !!src && formats.includes(src.format);
  const others = src ? formats.filter(f => f !== src.format) : [];

  const itemLine = (it: Item): React.ReactNode => {
    if (it.kind === 'campaign') {
      return <>
        {fmtDate(it.created_at)} · {it.content_mode === 'template' ? (it.template_name || it.template_key) : MODE_LABEL[it.content_mode] || it.content_mode}
        {it.queued ? ` · ${Number(it.queued).toLocaleString()} people` : ''}
      </>;
    }
    if (it.kind === 'design') return it.description || (it.builtin ? 'Built-in design' : 'Saved design');
    return <>{it.group ? `${it.group} · ` : ''}{it.subject}</>;
  };

  return (
    <Drawer title={title || 'Start from…'} onClose={onClose}
      subtitle="Pick an e-mail to start from. Nothing is changed until you choose how to use it.">
      <div className="flex flex-wrap gap-1">
        {tabs.map(t => (
          <button key={t} type="button" onClick={() => {
            // A load still in flight for the old tab must not land on this one.
            seq.current++;
            setTab(t); setSel(null); setSrc(null); setSrcErr(''); setView(null); setViewErr('');
          }}
            className={`py-1.5 px-3 rounded-full text-xs font-grotesk ${tab === t
              ? 'bg-primary-500 text-grayscale-100 font-bold'
              : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-[16rem_minmax(0,1fr)] min-h-0">
        <div className="flex flex-col gap-2 min-w-0">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className={inputCls} />
          {listErr && <Msg kind="error">{listErr}</Msg>}
          <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto pr-1">
            {listLoading && !items.length ? <Loading /> : items.map(it => (
              <button key={`${it.kind}:${it.ref}`} type="button" onClick={() => open(it)}
                className={`text-left rounded-lg border px-3 py-2 flex flex-col gap-0.5 ${
                  sel?.ref === it.ref && sel?.kind === it.kind ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:bg-grayscale-100'}`}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium text-grayscale-1300 break-words">{it.name}</span>
                  {it.format && it.kind !== 'campaign' && <Badge tone="grey">{FORMAT_LABEL[it.format as BodyFormat] || it.format}</Badge>}
                  {it.version ? <Badge tone="grey">v{it.version}</Badge> : null}
                  {it.memac && <Badge>MEMAC</Badge>}
                  {it.from_memac && <Badge>MEMAC</Badge>}
                  {it.builtin && <Badge tone="grey">built-in</Badge>}
                </div>
                <div className="text-[11px] text-grayscale-1100 truncate">{itemLine(it)}</div>
              </button>
            ))}
            {!listLoading && !items.length && !listErr && (
              <div className="text-xs text-grayscale-1100 py-3 text-center">
                {tab === 'library' && !qLive ? 'No saved templates yet.' : 'Nothing matches.'}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 min-w-0">
          {!sel && <div className="text-sm text-grayscale-1100 py-6 text-center">Pick one on the left to see it.</div>}
          {sel && !src && !srcErr && <Loading label="Loading…" />}
          {srcErr && <Msg kind="error">{srcErr}</Msg>}
          {sel && src && (
            <>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold text-grayscale-1300 font-grotesk">{src.name}</span>
                  <Badge tone="grey">{FORMAT_LABEL[src.format]}</Badge>
                  {src.source?.version ? <Badge tone="grey">v{src.source.version}</Badge> : null}
                  {src.category && <Badge tone="grey">{src.category.replace(/_/g, ' ')}</Badge>}
                </div>
                {src.warnings?.length > 0 && (
                  <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                    {src.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 rounded-lg border border-grayscale-300 bg-white p-3">
                {canRef && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Btn kind="primary" small onClick={() => onPick(src, 'reference')}>
                      Use the template itself
                    </Btn>
                    <span className="text-xs text-grayscale-1100">
                      Linked: later edits to “{src.kind === 'campaign' ? (sel?.template_name || src.template_key) : src.name}” are sent too.
                    </span>
                  </div>
                )}
                {(src.notes || []).length > 0 && (
                  <div className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
                    {(src.notes || []).map((n, i) => <div key={i}>{n}</div>)}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {ownOk ? (
                    <Btn kind={canRef ? 'subtle' : 'primary'} small disabled={!!busy} onClick={() => copyAs(src.format)}>
                      Copy its words here
                    </Btn>
                  ) : (
                    <span className="text-xs text-grayscale-1100">
                      It is {FORMAT_LABEL[src.format].toLowerCase()}; this editor takes {formats.map(f => FORMAT_LABEL[f].toLowerCase()).join(' or ')}:
                    </span>
                  )}
                  {others.map(f => (
                    <Btn key={f} small disabled={!!busy} onClick={() => copyAs(f)}>
                      {busy === f ? 'Converting…' : `Copy as ${FORMAT_LABEL[f]}`}
                    </Btn>
                  ))}
                  <span className="text-xs text-grayscale-1100">
                    A copy is yours to change; the original is not touched.
                  </span>
                </div>
              </div>
              {viewErr && <Msg kind="error">Preview: {viewErr}</Msg>}
              {view && <VaryingNotices varying={view.varying} />}
              {view ? (
                <EmailPreview subject={view.subject} html={view.html} text={view.text}
                  from={fmtFrom(src.from_name, src.from_email) || fmtFrom(view.from_name, view.from_email)}
                  replyTo={src.reply_to || undefined} height={460}
                  banner={<div className="text-xs text-grayscale-1100">
                    Rendered with sample values{memacCategory ? ', with the MEMAC unsubscribe line' : ''}; links are samples.
                  </div>} />
              ) : !viewErr && <Loading label="Rendering…" />}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Save as a new template (library_save)
// ---------------------------------------------------------------------------
type Cat = { key: string; label: string };
const FALLBACK_CATEGORIES: Cat[] = [
  { key: 'service', label: 'Service' }, { key: 'user_activity', label: 'User activity' },
  { key: 'new_data', label: 'New data' }, { key: 'features', label: 'New features & upgrades' },
  { key: 'marketing', label: 'Marketing & promotions' }, { key: 'industry_news', label: 'Industry news' },
  { key: 'billing', label: 'Billing' },
];

/** Name + category (+ description) → library_save, as a new saved template. */
export const SaveAsTemplate: React.FC<{
  content: EditableContent;
  envelope: Envelope;
  /** What each placeholder becomes for someone with no value (the editor's), kept with the template. */
  fallbacks?: Record<string, string>;
  origin: 'library' | 'campaigns' | 'memac';
  /** Where the words came from (kept on the template, display only). */
  source?: SourceRef | null;
  defaultName?: string;
  defaultCategory?: string;
  defaultDescription?: string;
  title?: string;
  /** One line under the title (what happens next). */
  note?: React.ReactNode;
  onSaved: (t: LibraryRow, warnings: string[]) => void;
  onClose: () => void;
}> = ({ content, envelope, fallbacks, origin, source, defaultName, defaultCategory, defaultDescription, title, note,
        onSaved, onClose }) => {
  const [name, setName] = useState((defaultName || '').slice(0, 120));
  const [category, setCategory] = useState(defaultCategory || '');
  const [description, setDescription] = useState(defaultDescription || '');
  const [cats, setCats] = useState<Cat[]>(FALLBACK_CATEGORIES);
  const [taken, setTaken] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let gone = false;
    panelGet('email', 'library_list', { include_archived: 1 }).then(({ data }) => {
      if (gone || !data) return;
      const d: any = data;
      if (Array.isArray(d.categories) && d.categories.length) setCats(d.categories);
      setTaken((d.templates || []).map((t: any) => String(t.name || '').toLowerCase()));
    });
    return () => { gone = true; };
  }, []);
  const clash = !!name.trim() && taken.includes(name.trim().toLowerCase());
  // The server keeps those for the placeholders the e-mail uses.
  const fb = Object.fromEntries(Object.entries(fallbacks || {}).filter(([, v]) => v !== undefined && v !== null));
  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true); setErr('');
    const { data, error } = await panelPost('email', 'library_save', toLibraryPayload(content, envelope, {
      name, description, category, origin, source: source || null, fallbacks: fb }));
    setBusy(false);
    if (error) { setErr(error); return; }
    onSaved((data as any).template as LibraryRow, ((data as any).warnings || []) as string[]);
  };
  return (
    <Modal title={title || 'Save as a new template'} onClose={onClose}
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" onClick={save} disabled={busy || !name.trim() || clash}>{busy ? 'Saving…' : 'Save template'}</Btn>
      </>}>
      {note && <p className="text-xs text-grayscale-1100">{note}</p>}
      <Labeled label="Name (shown in every template list)">
        <input autoFocus value={name} maxLength={120} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); }} className={inputCls} />
      </Labeled>
      {clash && <div className="text-xs text-red-700">A saved template already has this name (archived ones count too).</div>}
      <Labeled label="Category (who may unsubscribe from it; service and billing cannot be unsubscribed from)">
        <select value={category} onChange={e => setCategory(e.target.value)} className={inputCls}>
          <option value="">No category</option>
          {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </Labeled>
      <Labeled label="Description (optional)">
        <input value={description} maxLength={500} onChange={e => setDescription(e.target.value)} className={inputCls} />
      </Labeled>
      <div className="text-xs text-grayscale-1100">
        {FORMAT_LABEL[content.format]} · {envelope.from_email || 'Founders inbox@ (the default sender)'}.
        It is checked as strictly as a send: unknown placeholders and unapproved senders are refused.
      </div>
      {Object.keys(fb).length > 0 && (
        <div className="text-xs text-grayscale-1100">
          Kept with it, for someone with no value (the ones it uses): {Object.entries(fb).map(([k, v]) => `{${k}} → “${v}”`).join(' · ')}.
          A trigger or campaign sending it can set its own on top.
        </div>
      )}
      {err && <Msg kind="error">{err}</Msg>}
    </Modal>
  );
};

export default TemplatePicker;
